import { TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type {
  Bracket,
  Match,
  Pairing,
  Slot,
} from "@/lib/tool-kit/random-fun/brackets";

/** The strings these views draw, supplied by the widget. */
export interface BracketViewStrings {
  winnerOf: string;
  loserOf: string;
  byeSlot: string;
  roundFinal: string;
  roundSemi: string;
  roundQuarter: string;
  roundN: string;
  winnersFinal: string;
  winnersSide: string;
  losersSide: string;
  losersFinal: string;
  losersRound: string;
  grandFinal: string;
  matchLabel: string;
  roundOfRobin: string;
  rests: string;
  versus: string;
  standingsHeading: string;
  colTeam: string;
  colPlayed: string;
  colWon: string;
  colDrawn: string;
  colLost: string;
  colPoints: string;
}

type Side = "W" | "L" | "G";

function roundTitle(
  side: Side,
  fromEnd: number,
  round: number,
  double: boolean,
  strings: BracketViewStrings,
  fmt: (n: number) => string
): string {
  if (side === "G") {
    return strings.grandFinal;
  }
  if (side === "L") {
    return fromEnd === 0
      ? strings.losersFinal
      : t(strings.losersRound, { n: fmt(round) });
  }
  if (fromEnd === 0) {
    return double ? strings.winnersFinal : strings.roundFinal;
  }
  if (fromEnd === 1) {
    return strings.roundSemi;
  }
  if (fromEnd === 2) {
    return strings.roundQuarter;
  }
  return t(strings.roundN, { n: fmt(round) });
}

function slotText(
  slot: Slot,
  names: readonly string[],
  strings: BracketViewStrings
): string {
  switch (slot.kind) {
    case "entrant":
      return `${slot.seed}. ${names[slot.seed - 1] ?? ""}`;
    case "winner":
      return t(strings.winnerOf, { match: slot.match });
    case "loser":
      return t(strings.loserOf, { match: slot.match });
    default:
      return strings.byeSlot;
  }
}

function MatchBox({
  match,
  names,
  strings,
}: {
  match: Match;
  names: readonly string[];
  strings: BracketViewStrings;
}) {
  const open = (slot: Slot) => slot.kind !== "entrant";
  return (
    <li className="break-inside-avoid overflow-hidden rounded-md border border-border bg-card text-sm">
      <p className="border-border border-b bg-muted/40 px-2 py-0.5 text-muted-foreground text-xs">
        {t(strings.matchLabel, { id: match.id })}
      </p>
      {[match.a, match.b].map((slot, index) => (
        <p
          className={`px-2 py-1.5 ${index === 0 ? "border-border border-b" : ""} ${
            open(slot)
              ? "text-muted-foreground italic"
              : "font-medium text-foreground"
          }`}
          // A match always has exactly two slots, in a fixed order.
          // biome-ignore lint/suspicious/noArrayIndexKey: two fixed slots
          key={index}
        >
          {slotText(slot, names, strings)}
        </p>
      ))}
    </li>
  );
}

function SideColumns({
  matches,
  names,
  double,
  strings,
  fmt,
}: {
  matches: readonly Match[];
  names: readonly string[];
  double: boolean;
  strings: BracketViewStrings;
  fmt: (n: number) => string;
}) {
  const rounds = new Map<number, Match[]>();
  for (const match of matches) {
    rounds.set(match.round, [...(rounds.get(match.round) ?? []), match]);
  }
  return (
    <div className="flex gap-4 overflow-x-auto pb-2 print:overflow-visible">
      {[...rounds.entries()].map(([round, list]) => {
        const first = list[0] as Match;
        return (
          <div className="flex min-w-48 flex-col gap-2" key={round}>
            <h4 className="font-medium text-foreground text-sm">
              {roundTitle(
                first.side,
                first.fromEnd,
                round,
                double,
                strings,
                fmt
              )}
            </h4>
            <ul className="flex flex-1 flex-col justify-around gap-3">
              {list.map((match) => (
                <MatchBox
                  key={match.id}
                  match={match}
                  names={names}
                  strings={strings}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function EliminationView({
  bracket,
  names,
  double,
  strings,
  fmt,
  resetNote,
}: {
  bracket: Bracket;
  names: readonly string[];
  double: boolean;
  strings: BracketViewStrings;
  fmt: (n: number) => string;
  resetNote: string;
}) {
  const side = (which: Side) =>
    bracket.matches.filter((match) => match.side === which);
  const column = (which: Side) => (
    <SideColumns
      double={double}
      fmt={fmt}
      matches={side(which)}
      names={names}
      strings={strings}
    />
  );
  if (!double) {
    return column("W");
  }
  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.winnersSide}</h3>
        {column("W")}
      </section>
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.losersSide}</h3>
        {column("L")}
      </section>
      <section className="space-y-2">
        {column("G")}
        <p className="text-muted-foreground text-xs">{resetNote}</p>
      </section>
    </div>
  );
}

export function RoundRobinView({
  rounds,
  names,
  strings,
  fmt,
}: {
  rounds: readonly (readonly Pairing[])[];
  names: readonly string[];
  strings: BracketViewStrings;
  fmt: (n: number) => string;
}) {
  const name = (index: number | null) =>
    index === null ? "" : (names[index] ?? "");
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rounds.map((round, r) => (
          <section
            className="break-inside-avoid rounded-lg border border-border bg-card p-3"
            // Rounds are positional.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rounds
            key={r}
          >
            <h4 className="mb-2 font-medium text-foreground text-sm">
              {t(strings.roundOfRobin, { n: fmt(r + 1) })}
            </h4>
            <ul className="space-y-1 text-foreground text-sm">
              {round.map((pair, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional pairings
                <li key={i}>
                  {pair.home === null || pair.away === null
                    ? t(strings.rests, {
                        team: name(pair.home === null ? pair.away : pair.home),
                      })
                    : t(strings.versus, {
                        home: name(pair.home),
                        away: name(pair.away),
                      })}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <section className="break-inside-avoid space-y-2">
        <h3 className={WIDGET_H3}>{strings.standingsHeading}</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                {[
                  strings.colTeam,
                  strings.colPlayed,
                  strings.colWon,
                  strings.colDrawn,
                  strings.colLost,
                  strings.colPoints,
                ].map((label) => (
                  <th className={TH} key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {names.map((team, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: names may repeat
                <tr key={i}>
                  <td className={TD}>{team}</td>
                  {[0, 1, 2, 3, 4].map((cell) => (
                    <td className={`${TD} h-9 min-w-12`} key={cell} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
